import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import EventSystemTabLayout from '../../components/layout/EventSystemTabLayout';
import { toast } from 'react-toastify';
import { FiSave, FiAlertCircle, FiCheckCircle, FiPlay, FiHelpCircle } from 'react-icons/fi';
import { fetchWithAuth } from '../../api/backendApi';
import { useTenant } from '@libs/contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { readDealerAdminSession } from './dealerAdminSession';
import { readSupportedTenantSession } from './supportedTenantsSession';
import { isMissingSipConfigError, sipDomainAssigned } from './sipConfigLoad';
import SipDomainRequestCard from './SipDomainRequestCard';
import SipSetupPanel from './SipSetupPanel';

const CARD_CLASS = 'bg-white border border-gray-300 shadow-md rounded-lg p-6';
const CONTROL_BASE_CLASS = 'block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';
const INPUT_CLASS = CONTROL_BASE_CLASS;
const SELECT_CLASS = CONTROL_BASE_CLASS;
const TOGGLE_ROW_CLASS = 'flex items-center gap-3 py-3 px-4 border border-gray-300 rounded-md bg-gray-50';

const VISIBLE_DEFAULTS = {
  sipDomainSid: '',
  domainFqdn: '',
  preferredTransport: 'TLS',
  extensionLength: 4,
  normalizeExtensions: true,
};

const toStringList = (value, fallback) => {
  if (typeof value === 'string') {
    return value.split('\n').map((s) => s.trim()).filter(Boolean);
  }
  if (Array.isArray(value)) return value.filter(Boolean);
  if (Array.isArray(fallback)) return fallback.filter(Boolean);
  return [];
};

const toFormValues = (data = {}) => ({
  ...VISIBLE_DEFAULTS,
  sipDomainSid: data.sipDomainSid || '',
  domainFqdn: data.domainFqdn || '',
  preferredTransport: data.preferredTransport || 'TLS',
  extensionLength: typeof data.extensionLength === 'number' ? data.extensionLength : 4,
  normalizeExtensions: data.normalizeExtensions !== false,
});

/**
 * SIP Interworking Settings Tab (Embeddable)
 *
 * Core UI for tenant-scoped SIP configuration. Can be rendered inside other pages.
 */
const SipInterworkingTab = ({ tenantId: overrideTenantId, tenantName = '' }) => {
  const { selectedTenant, loading: tenantLoading } = useTenant();
  const { userData, userClaims } = useAuth();
  const userRole = userClaims?.role || userData?.role;
  const canManageSip =
    userRole === 'superadmin' || userRole === 'admin' || userRole === 'tenant_admin';
  const canAssignDomain = userRole === 'superadmin' || userRole === 'admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [missingDomain, setMissingDomain] = useState(false);
  const [config, setConfig] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [showHelpSidebar, setShowHelpSidebar] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  const [autoSaving, setAutoSaving] = useState(false);

  const tenantId = overrideTenantId || selectedTenant?.id;
  const supportSession = readDealerAdminSession() ? null : readSupportedTenantSession();
  const supportView = Boolean(supportSession && supportSession.accessLevel !== 'manage');
  const dealerSession = Boolean(readDealerAdminSession());
  const canProvisionSip = !supportView && (canManageSip || dealerSession);
  const canVerifySip = canManageSip || dealerSession || Boolean(supportSession);

  const { register, handleSubmit, watch, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: VISIBLE_DEFAULTS,
  });

  const formValues = watch();

  useEffect(() => {
    const restoreFormState = () => {
      try {
        const savedState = sessionStorage.getItem(`sipConfig_draft_${tenantId}`);
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          reset(toFormValues({ ...VISIBLE_DEFAULTS, ...parsedState }));
          toast.info('Draft restored from previous session', { duration: 2000 });
        }
      } catch (error) {
        console.error('Error restoring form state:', error);
      }
    };

    if (tenantId && canManageSip) {
      restoreFormState();
    }
  }, [tenantId, reset, canManageSip]);

  useEffect(() => {
    if (!canManageSip || !tenantId || !isDirty) return;

    setAutoSaving(true);
    const timeoutId = setTimeout(() => {
      try {
        sessionStorage.setItem(`sipConfig_draft_${tenantId}`, JSON.stringify(formValues));
        setAutoSaving(false);
      } catch (error) {
        console.error('Error auto-saving form state:', error);
        setAutoSaving(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      setAutoSaving(false);
    };
  }, [formValues, tenantId, isDirty, canManageSip]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (canManageSip && isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, canManageSip]);

  useEffect(() => {
    if (tenantLoading && !overrideTenantId) {
      return;
    }

    if (!tenantId) {
      setLoading(false);
      return;
    }

    fetchSipConfig();
  }, [tenantId, tenantLoading, overrideTenantId]);

  const fetchSipConfig = async (silent = false) => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/v2/tenants/${tenantId}/sip-config`);

      if (response.success && response.data) {
        setConfig(response.data);
        reset(toFormValues(response.data));
        setMissingDomain(!sipDomainAssigned(response.data));
      } else {
        setConfig(null);
        reset(VISIBLE_DEFAULTS);
        setMissingDomain(true);
      }
    } catch (error) {
      if (isMissingSipConfigError(error)) {
        setConfig(null);
        reset(VISIBLE_DEFAULTS);
        setMissingDomain(true);
      } else {
        console.error('Error fetching SIP config:', error);
        setMissingDomain(false);
        if (!silent) {
          toast.error('Failed to load SIP configuration');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!canManageSip) return;
    try {
      setSaving(true);
      setWarnings([]);

      // PUT replaces the whole doc. Merge stored config first so hidden
      // live fields (registrationExpiry, sipDialTimeout, etc.) are not wiped.
      const payload = {
        ...(config || {}),
        ...data,
        tenantId,
      };

      if (typeof payload.blockedPrefixes === 'string') {
        payload.blockedPrefixes = toStringList(payload.blockedPrefixes, config?.blockedPrefixes);
      }
      if (typeof payload.emergencyPatterns === 'string') {
        payload.emergencyPatterns = toStringList(payload.emergencyPatterns, config?.emergencyPatterns);
      }
      if (typeof payload.autoAnswerExtensions === 'string') {
        payload.autoAnswerExtensions = toStringList(payload.autoAnswerExtensions, config?.autoAnswerExtensions);
      }
      if (typeof payload.ipAllowlist === 'string') {
        payload.ipAllowlist = toStringList(payload.ipAllowlist, config?.ipAllowlist);
      }

      const response = await fetchWithAuth(
        `/api/v2/tenants/${tenantId}/sip-config`,
        'PUT',
        payload
      );

      if (response.success) {
        toast.success('SIP configuration saved successfully');
        if (response.data.warnings && response.data.warnings.length > 0) {
          setWarnings(response.data.warnings.filter((w) =>
            !/international dialing is disabled|sessionTimer exceeds registrationExpiry/i.test(w)
          ));
        }

        const saved = response.data.config || payload;
        setConfig(saved);
        reset(toFormValues(saved));

        try {
          sessionStorage.removeItem(`sipConfig_draft_${tenantId}`);
        } catch (error) {
          console.error('Error clearing draft:', error);
        }

        await fetchSipConfig(true);
      } else {
        throw new Error(response.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving SIP config:', error);
      toast.error(error.details || error.message || 'Failed to save SIP configuration');
    } finally {
      setSaving(false);
    }
  };

  const validationStatus = useMemo(() => ({
    isValid: Object.keys(errors).length === 0,
    issues: Object.values(errors).map((err) => err?.message).filter(Boolean),
  }), [errors]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading SIP configuration...</div>
      </div>
    );
  }

  if (!tenantId && !tenantLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-yellow-600">Please select a tenant to view SIP Interworking configuration.</div>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">No tenant ID found. Please contact support.</div>
      </div>
    );
  }

  if (missingDomain && !canProvisionSip) {
    const supportSession = readDealerAdminSession() ? null : readSupportedTenantSession();
    return (
      <SipDomainRequestCard
        tenantName={tenantName || supportSession?.name || ''}
        tenantId={tenantId}
      />
    );
  }

  return (
      <div className="flex gap-6">
        <div className="flex-1 space-y-6">
        <div className={CARD_CLASS}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">SIP Interworking</h1>
              <p className="mt-1 text-sm text-gray-500">
                Twilio SIP domain and extension routing. Outbound phone-number calling and rate limits are on the PSTN tab.
              </p>
              {!canManageSip && (
                <p className="mt-2 text-sm text-gray-600">
                  SIP domain settings are view-only for this role. Use SIP Page Zone to add paging endpoints.
                </p>
              )}
            </div>

            {canManageSip && (
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setShowHelpSidebar(!showHelpSidebar)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiHelpCircle className="w-4 h-4 mr-2" />
                {showHelpSidebar ? 'Hide Help' : 'Show Help'}
              </button>
              {autoSaving && (
                <div className="inline-flex items-center space-x-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-blue-700">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm font-medium">Auto-saving...</span>
                </div>
              )}
              {missingDomain ? (
                <div className="inline-flex items-center space-x-2 rounded-full border border-yellow-200 bg-yellow-50 px-3 py-1 text-yellow-700">
                  <FiAlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">No SIP domain assigned</span>
                </div>
              ) : validationStatus.isValid ? (
                <div className="inline-flex items-center space-x-2 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-green-700">
                  <FiCheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Configuration Valid</span>
                </div>
              ) : (
                <div className="inline-flex items-center space-x-2 rounded-full border border-yellow-200 bg-yellow-50 px-3 py-1 text-yellow-700">
                  <FiAlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{validationStatus.issues.length} Issue(s)</span>
                </div>
              )}
            </div>
            )}
          </div>
        </div>

        <SipSetupPanel tenantId={tenantId} canManage={canProvisionSip} canVerify={canVerifySip} />

        {canManageSip && validationStatus.issues.length > 0 && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
            <div className="flex">
              <FiAlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Configuration Issues</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {validationStatus.issues.map((issue, idx) => (
                      <li key={idx}>{issue}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-4">
            <div className="flex">
              <FiAlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Warnings</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {canManageSip && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg">
          <nav className="-mb-px flex space-x-8 px-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('config')}
              className={`${
                activeTab === 'config'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('diagnostics')}
              className={`${
                activeTab === 'diagnostics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Diagnostics & Testing
            </button>
          </nav>
        </div>
        )}

        {canManageSip && activeTab === 'diagnostics' ? (
          <DiagnosticsTab
            tenantId={tenantId}
            config={config}
          />
        ) : (
          <ConfigurationTab
            register={register}
            watch={watch}
            errors={errors}
            config={config}
            canManageSip={canManageSip}
          />
        )}

        {activeTab === 'config' && canManageSip && (
          <div className="flex items-center justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={() => {
                if (config) {
                  reset(toFormValues(config));
                } else {
                  reset(VISIBLE_DEFAULTS);
                }
              }}
              disabled={!isDirty || saving}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={saving || !validationStatus.isValid}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSave className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        )}
        </div>

        {showHelpSidebar && canManageSip && <HelpSidebar />}
      </div>
  );
};

const SipInterworkingPage = () => {
  const { selectedTenant, loading: tenantLoading } = useTenant();
  const tenantId = selectedTenant?.id;
  if (tenantLoading) {
    return (
      <EventSystemTabLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading SIP configuration...</div>
        </div>
      </EventSystemTabLayout>
    );
  }
  return (
    <EventSystemTabLayout>
      <SipInterworkingTab tenantId={tenantId} />
    </EventSystemTabLayout>
  );
};

const ConfigurationTab = ({ register, watch, errors, config, canManageSip = false }) => {
  return (
    <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
      <fieldset disabled={!canManageSip} className="space-y-8">
        <SectionDomainTransport register={register} watch={watch} config={config} canManageSip={canManageSip} />
        {canManageSip && (
          <SectionDialingNormalization register={register} errors={errors} />
        )}
      </fieldset>
    </form>
  );
};

const DiagnosticsTab = ({ tenantId, config }) => {
  return (
    <div className="space-y-6">
      <DialPlanSimulator tenantId={tenantId} config={config} />
    </div>
  );
};

const SectionDomainTransport = ({ register, watch, config, canManageSip = false }) => {
  const transport = watch('preferredTransport');
  const transportHelper = useMemo(() => {
    switch (transport) {
      case 'TLS':
        return 'Used when provisioning panel SIP identities. Standard TLS port is 5061.';
      case 'TCP':
        return 'Used when provisioning panel SIP identities. TCP typically listens on port 5060.';
      case 'UDP':
        return 'Used when provisioning panel SIP identities. UDP typically listens on port 5060.';
      default:
        return 'Select the transport used when provisioning SIP identities.';
    }
  }, [transport]);

  return (
    <div className={CARD_CLASS}>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Domain & Transport</h2>
      <p className="text-sm text-gray-500 mb-4">
        Link this tenant to its Twilio SIP Domain so inbound SIP calls can be routed correctly.
      </p>
      <div className="grid grid-cols-4 gap-y-4 gap-x-6 ">
        {canManageSip && (
        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SIP Domain SID
          </label>
          <input
            type="text"
            {...register('sipDomainSid')}
            defaultValue={config?.sipDomainSid || ''}
            placeholder="SDXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
            className={INPUT_CLASS}
          />
          <p className="mt-1 text-xs text-gray-500">Paste the Twilio SIP Domain SID (format SDXXXXXXXX...).</p>
        </div>
        )}

        <div className="sm:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Domain FQDN
          </label>
          <input
            type="text"
            {...register('domainFqdn')}
            defaultValue={config?.domainFqdn || ''}
            placeholder="your-domain.sip.twilio.com"
            className={INPUT_CLASS}
          />
          <p className="mt-1 text-xs text-gray-500">Full SIP domain hostname (e.g., yourdomain.sip.twilio.com).</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Transport
          </label>
          <select
            {...register('preferredTransport')}
            defaultValue={config?.preferredTransport || 'TLS'}
            className={SELECT_CLASS}
          >
            <option value="UDP">UDP</option>
            <option value="TCP">TCP</option>
            <option value="TLS">TLS (Recommended)</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">{transportHelper}</p>
        </div>
      </div>
    </div>
  );
};

const SectionDialingNormalization = ({ register, errors }) => {
  return (
    <div className={CARD_CLASS}>
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Extensions</h2>
      <p className="text-sm text-gray-500 mb-4">
        How the SIP router decides a dial is an internal extension instead of a phone number.
      </p>
      <div className="grid grid-cols-2 gap-y-4 gap-x-6 sm:grid-cols-4 lg:grid-cols-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Extension Length
          </label>
          <input
            type="number"
            {...register('extensionLength', {
              required: 'Required',
              valueAsNumber: true,
              min: { value: 3, message: 'Min 3' },
              max: { value: 6, message: 'Max 6' }
            })}
            min={3}
            max={6}
            className={INPUT_CLASS}
          />
          {errors.extensionLength && (
            <p className="mt-1 text-xs text-red-600">{errors.extensionLength.message}</p>
          )}
        </div>

        <div className={`${TOGGLE_ROW_CLASS} sm:col-span-2 lg:col-span-2`}>
          <input
            type="checkbox"
            {...register('normalizeExtensions')}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-700 col-span-2 whitespace-nowrap">
            Normalize Extensions
          </label>
        </div>
      </div>
    </div>
  );
};

const DialPlanSimulator = ({ tenantId }) => {
  const [dialString, setDialString] = useState('');
  const [result, setResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  const handleSimulate = async () => {
    if (!dialString.trim()) {
      toast.error('Please enter a dial string');
      return;
    }

    try {
      setSimulating(true);
      const response = await fetchWithAuth(
        `/api/v2/tenants/${tenantId}/sip-simulate`,
        'POST',
        {
          dialString: dialString.trim(),
          mode: 'auto'
        }
      );

      if (response.success) {
        setResult(response.data);
      } else {
        throw new Error(response.error || 'Simulation failed');
      }
    } catch (error) {
      console.error('Error simulating dial:', error);
      toast.error(error.message || 'Failed to simulate dial plan');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className={CARD_CLASS}>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Dial Plan Simulator</h2>
      <p className="text-sm text-gray-500 mb-4">Test dial string normalization without placing a real call</p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dial String
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={dialString}
              onChange={(e) => setDialString(e.target.value)}
              placeholder="5002, +15551234567, or sip:user@domain"
              className="block flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
            <button
              type="button"
              onClick={handleSimulate}
              disabled={simulating}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <FiPlay className="w-4 h-4 mr-2" />
              {simulating ? 'Running...' : 'Run'}
            </button>
          </div>
        </div>

        {result && (
          <div className="rounded-md bg-gray-50 border border-gray-200 p-4">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Normalized Target:</span>
                <p className="mt-1 font-mono text-gray-900">{result.normalizedTarget}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Branch:</span>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    result.branch === 'sip' ? 'bg-blue-100 text-blue-800' :
                    result.branch === 'pstn' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {result.branch.toUpperCase()}
                  </span>
                </p>
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-700">Generated TwiML:</span>
                <pre className="mt-1 p-2 bg-white border border-gray-200 rounded text-xs overflow-x-auto">
                  {result.generatedTwiml}
                </pre>
              </div>
              {result.warnings && result.warnings.length > 0 && (
                <div className="col-span-2">
                  <span className="font-medium text-yellow-700">Warnings:</span>
                  <ul className="mt-1 list-disc pl-5 text-yellow-600">
                    {result.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const HelpSidebar = () => {
  const [expandedSection, setExpandedSection] = useState('domain');

  const helpSections = [
    {
      id: 'domain',
      title: 'Domain & Transport',
      icon: '🌐',
      items: [
        {
          field: 'SIP Domain SID',
          description: 'Twilio SIP Domain SID (starts with SD). Links this tenant to its Twilio domain.',
          example: 'SD071f697d51b5c7610166b5b1dd889e09'
        },
        {
          field: 'Domain FQDN',
          description: 'The fully qualified domain name of your Twilio SIP Domain. Inbound and outbound SIP routing use this host.',
          example: 'scottfarrell.sip.twilio.com'
        },
        {
          field: 'Preferred Transport',
          description: 'Transport written onto newly provisioned panel SIP identities. It does not change the Twilio domain itself.',
          example: 'TLS (recommended)'
        }
      ]
    },
    {
      id: 'dialing',
      title: 'Extensions',
      icon: '📞',
      items: [
        {
          field: 'Extension Length',
          description: 'Digit length treated as an internal extension instead of a phone number.',
          example: '4 (for extensions like 5001, 5002)'
        },
        {
          field: 'Normalize Extensions',
          description: 'When enabled, numeric dials of the extension length are routed as SIP users on this domain.',
          example: 'Enabled'
        }
      ]
    }
  ];

  return (
    <div className="w-96 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden flex flex-col max-h-[calc(100vh-12rem)] sticky top-6">
      <div className="bg-blue-600 text-white px-4 py-3 border-b border-blue-700">
        <div className="flex items-center space-x-2">
          <FiHelpCircle className="w-5 h-5" />
          <h3 className="font-semibold text-lg">Field Guide</h3>
        </div>
        <p className="text-xs text-blue-100 mt-1">Click a section to view field descriptions</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="divide-y divide-gray-200">
          {helpSections.map((section) => (
            <div key={section.id} className="border-b border-gray-200">
              <button
                onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{section.icon}</span>
                  <span className="font-medium text-gray-900 text-sm">{section.title}</span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedSection === section.id ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedSection === section.id && (
                <div className="px-4 pb-4 space-y-4 bg-gray-50">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="bg-white rounded-md p-3 border border-gray-200">
                      <div className="flex items-start space-x-2">
                        <div className="flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">{item.field}</h4>
                          <p className="text-xs text-gray-600 leading-relaxed mb-2">{item.description}</p>
                          {item.example && (
                            <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                              <p className="text-xs text-blue-900">
                                <span className="font-medium">Example:</span> {item.example}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
        <div className="flex items-start space-x-2 text-xs text-gray-600">
          <FiAlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" />
          <p>
            <span className="font-medium">Tip:</span> Drafts auto-save as you type. Click Save Configuration to apply changes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SipInterworkingPage;
export { SipInterworkingTab };
